import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import type { RegistrationGateListKind } from "./RegistrationGateAddModal";
import styles from "./RegistrationGate.module.css";

type RemoveCopy = {
  title: string;
  /** Section-specific confirm copy, interpolating the stored value being removed. */
  confirm: (value: string) => string;
  confirmLabel: string;
  confirmingLabel: string;
};

const REMOVE_COPY: Record<RegistrationGateListKind, RemoveCopy> = {
  blocklist: {
    title: "Remove blocked domain",
    confirm: value =>
      `Remove "${value}" from the blocklist? People with this email domain will be able to register again.`,
    confirmLabel: "Remove domain",
    confirmingLabel: "Removing...",
  },
  allowlist: {
    title: "Remove allow-list term",
    confirm: value =>
      `Remove "${value}" from the profanity allow-list? Email domains and company names containing it may be rejected as profane.`,
    confirmLabel: "Remove term",
    confirmingLabel: "Removing...",
  },
};

export type RegistrationGateRemoveModalProps = {
  isOpen: boolean;
  /** Which list is being removed from — selects the confirm copy. */
  kind: RegistrationGateListKind;
  /** The stored value (domain/term) being removed — shown in the confirm copy. */
  value: string;
  /**
   * Wraps the Phase 1 remove service. The optional reason is passed through and
   * the service encodes it as a `?reason=` QUERY param (DELETE has no body).
   */
  remove: (reason?: string) => Promise<void>;
  /** Called on a successful remove (the section toasts + refetches). */
  onSuccess: () => void;
  onClose: () => void;
};

/**
 * Confirmation modal for the per-row Remove action on both Registration Gate sections.
 * Mirrors the RegistrationGateAddModal pattern: submits via the injected `remove`, and
 * on error keeps the modal open with an inline message (the row is left intact).
 */
export function RegistrationGateRemoveModal({
  isOpen,
  kind,
  value,
  remove,
  onSuccess,
  onClose,
}: RegistrationGateRemoveModalProps) {
  const copy = REMOVE_COPY[kind];
  const [reason, setReason] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset the form whenever the modal closes (mirrors RegistrationGateAddModal).
  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setServerError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  async function handleConfirm() {
    if (isSubmitting) return;
    setServerError(null);
    setIsSubmitting(true);

    try {
      const trimmedReason = reason.trim();
      await remove(trimmedReason.length > 0 ? trimmedReason : undefined);
      onSuccess();
    } catch (err) {
      // Keep the modal open and the row intact so the user can retry — never
      // silently swallow the failure (review lesson #3).
      setServerError(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={copy.title} size="small">
      <div className={styles.confirmBody}>
        {serverError && (
          <div className={styles.serverError} role="alert" aria-live="assertive">
            {serverError}
          </div>
        )}

        <p className={styles.confirmMessage}>{copy.confirm(value)}</p>

        <Input
          label="Reason (optional)"
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={isSubmitting}
          fullWidth
          autoComplete="off"
        />

        <div className={styles.formActions}>
          <Button
            variant="outlined"
            color="default"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? copy.confirmingLabel : copy.confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default RegistrationGateRemoveModal;
